'use client'

import { useState, useRef, useEffect, type ReactNode } from 'react'
import { AnimatePresence } from 'motion/react'
import Root from './Root'
import Sheet from '@/components/ui/Sheet'
import { usePageContext } from '@/components/content/common/PageContext'
import { toSlug } from '@/lib/utils'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { SectionItem } from '@/types/app'

type TreeProps = {
  roots: SectionItem[]
  header?: ReactNode
  footer?: ReactNode
  offset?: number
  toggle?: boolean
}

export default function Tree({ roots, header, footer, offset = 154, toggle = true }: TreeProps) {
  const { activeSection, sidebarVisible, setSidebarVisible } = usePageContext()

  const [targetHeight, setTargetHeight] = useState<number>(0)
  const [treeHeight, setTreeHeight] = useState<number>(0)

  const filteredRoots = roots.map((root) => ({
    ...root,
    children: root.children?.filter((child) => child.title) || [],
  }))
  const tree = useRef<HTMLUListElement>(null)
  const treeHeightRef = useRef<number>(treeHeight)

  const { md } = useBreakpoint()
  const isMobile = !md
  const responsiveOffset = offset + (isMobile ? (toggle ? 60 : 55) : 0)

  useEffect(() => {
    let calculatedHeight = 0

    if (activeSection) {
      const activeBranch = filteredRoots.find((root) => {
        const slug = toSlug(root.title ?? '')
        return activeSection.startsWith(`${slug}-`) || slug === activeSection
      })

      if (activeBranch?.children?.length) {
        const total = activeBranch.children.length
        const offset = total === 1 ? 18 : 0
        calculatedHeight = total < 6 ? total * 43 + offset : 250
      }
    }

    setTargetHeight(calculatedHeight)
  }, [roots, filteredRoots, activeSection])

  useEffect(() => {
    const calculateTreeHeight = () => {
      if (tree.current) {
        const { innerHeight } = window
        const sidebarHeight = tree.current.clientHeight
        const headerOffset = 100

        const availableHeight = innerHeight - headerOffset - sidebarHeight + treeHeightRef.current
        const newTreeHeight = Math.max(availableHeight, 0)

        const updatedTreeHeight = targetHeight <= newTreeHeight ? targetHeight : newTreeHeight

        setTimeout(() => {
          if (updatedTreeHeight !== treeHeightRef.current) {
            setTreeHeight(updatedTreeHeight)
          }
        }, 200)
      }
    }

    if (targetHeight > 0) {
      calculateTreeHeight()
      window.addEventListener('resize', calculateTreeHeight)
    }

    return () => {
      window.removeEventListener('resize', calculateTreeHeight)
    }
  }, [targetHeight])

  useEffect(() => {
    treeHeightRef.current = treeHeight
  }, [treeHeight])

  const treeContent = ({
    showHeader,
    showFooter,
  }: {
    showHeader: boolean
    showFooter: boolean
  }) => (
    <AnimatePresence mode="wait">
      <ul
        ref={tree}
        className="divide-y divide-gray-200 dark:divide-white/10 dark:border-white/10 md:rounded-2xl md:border md:border-gray-200"
      >
        {showHeader && header}
        {filteredRoots.map((root, index) => (
          <Root
            key={index}
            item={root}
            treeHeight={treeHeight}
            offset={responsiveOffset}
            isLast={index === roots.length - 1}
          />
        ))}
        {showFooter && footer}
      </ul>
    </AnimatePresence>
  )

  if (isMobile) {
    return (
      <Sheet
        trigger={
          <div className="flex h-14 w-full items-center border-b border-gray-200 bg-white/95 px-3 backdrop-blur-md dark:border-white/10 dark:bg-black/95">
            {header}
          </div>
        }
        isVisible={sidebarVisible}
        isMenu={true}
        close={() => setSidebarVisible(false)}
        indicatorClass="bg-zinc-50 dark:bg-white/5 [&>span]:bg-black/10 dark:[&>span]:bg-white/20"
      >
        {treeContent({ showHeader: false, showFooter: false })}
      </Sheet>
    )
  }

  return treeContent({ showHeader: true, showFooter: true })
}
